import React, { useEffect, useState } from "react";
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api, apiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";

const COLORS = ["#FF9933", "#003366", "#138808", "#E6892E", "#475569", "#93C5FD", "#FCD34D", "#FCA5A5"];

export default function Analytics() {
  const [days, setDays] = useState("7");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics", { params: { days: Number(days) } }).then(({ data }) => setData(data)).catch((e) => toast.error(apiError(e)));
  }, [days]);

  if (!data) return <div className="text-slate-400 font-semibold py-20 text-center">Loading analytics…</div>;

  return (
    <div data-testid="admin-analytics">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-black font-heading text-slate-900 tracking-tighter">Analytics</h1>
          <p className="text-sm text-slate-500 mt-1">Privacy-conscious first-party analytics. No third-party trackers.</p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger data-testid="analytics-range" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Today</SelectItem>
            <SelectItem value="2">Yesterday + Today</SelectItem>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { l: "Page Views", v: data.totals.views }, { l: "Unique Visitors", v: data.totals.unique_visitors },
          { l: "Registrations", v: data.funnel.registrations }, { l: "Completions", v: data.funnel.completions },
        ].map((k) => (
          <div key={k.l} className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="font-timer text-3xl font-bold text-slate-900">{k.v}</div>
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 mt-1">{k.l}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-xl p-6">
        <h2 className="font-heading font-bold text-slate-900 mb-1">Registration Funnel</h2>
        <div className="flex flex-wrap items-center gap-3 mt-4" data-testid="funnel">
          {[["Visitors", data.funnel.visitors], ["Registered", data.funnel.registrations], ["Quiz Starts", data.funnel.quiz_starts], ["Completed All", data.funnel.completions]].map(([l, v], i, arr) => (
            <React.Fragment key={l}>
              <div className="flex-1 min-w-[130px] rounded-xl border border-slate-200 p-4 text-center" style={{ background: `linear-gradient(180deg, rgba(255,153,51,${0.12 - i * 0.02}), #fff)` }}>
                <div className="font-timer text-2xl font-bold text-slate-900">{v}</div>
                <div className="text-[10px] font-bold tracking-[0.15em] uppercase text-slate-400">{l}</div>
                {i > 0 && <div className="text-[10px] font-bold text-[#138808] mt-1">{arr[i - 1][1] ? Math.round((v / arr[i - 1][1]) * 100) : 0}% conversion</div>}
              </div>
              {i < arr.length - 1 && <span className="text-slate-300 font-bold">→</span>}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h2 className="font-heading font-bold text-slate-900 mb-4">Traffic Over Time</h2>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={data.daily}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip />
              <Area type="monotone" dataKey="views" stroke="#FF9933" fill="#FF9933" fillOpacity={0.15} name="Views" />
              <Area type="monotone" dataKey="registrations" stroke="#138808" fill="#138808" fillOpacity={0.15} name="Registrations" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h2 className="font-heading font-bold text-slate-900 mb-4">Devices & Sources</h2>
          <div className="grid grid-cols-2 gap-4">
            <ResponsiveContainer width="100%" height={180}>
              <PieChart><Pie data={data.devices} dataKey="count" nameKey="name" innerRadius={40} outerRadius={70}>{data.devices.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip /></PieChart>
            </ResponsiveContainer>
            <div>
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 mb-2">Registration Sources</div>
              {data.sources.map((s, i) => (
                <div key={s.name} className="flex justify-between text-sm py-1.5 border-b border-slate-50">
                  <span className="flex items-center gap-2 text-slate-600 capitalize"><span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{s.name}</span>
                  <span className="font-timer font-bold">{s.count}</span>
                </div>
              ))}
              {data.sources.length === 0 && <p className="text-xs text-slate-400">No data yet.</p>}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h2 className="font-heading font-bold text-slate-900 mb-4">Category Performance</h2>
          {data.category_stats.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.category_stats}><CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" /><XAxis dataKey="category" tick={{ fontSize: 9 }} angle={-20} textAnchor="end" height={60} /><YAxis tick={{ fontSize: 10 }} /><Tooltip /><Bar dataKey="avg_score" fill="#003366" name="Avg Score" radius={[4, 4, 0, 0]} /></BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400">No attempts yet.</p>}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h2 className="font-heading font-bold text-slate-900 mb-4">Hardest Questions</h2>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {[...data.question_stats].sort((a, b) => a.correct_pct - b.correct_pct).slice(0, 10).map((q) => (
              <div key={q.question_id} className="flex items-center justify-between gap-3 text-sm bg-slate-50 rounded-lg px-4 py-2.5">
                <span className="text-slate-600 truncate">{q.text}</span>
                <span className="font-timer font-bold text-red-600 shrink-0">{q.correct_pct}% ✓</span>
              </div>
            ))}
            {data.question_stats.length === 0 && <p className="text-sm text-slate-400">No answers recorded yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
